import React, { useState, useEffect } from "react";
import { Snackbar, Alert } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Magic (Refine) icon as SVG (copied from SimpleEditor)
const MagicIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#5C3B8B" style={{ width: 18, height: 18, verticalAlign: 'middle', margin: '0 2px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
  </svg>
);

// Entity dot for entity instructions
const EntityDot = (
  <span style={{
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: '#8e5cff',
    marginRight: 4,
    verticalAlign: 'middle',
  }} />
);

const ExpandIcon = <ExpandMoreIcon sx={{ color: '#5C3B8B', fontSize: 18, verticalAlign: 'middle', margin: '0 2px' }} />;
const CollapseIcon = <ExpandLessIcon sx={{ color: '#5C3B8B', fontSize: 18, verticalAlign: 'middle', margin: '0 2px' }} />;

const FullGraphButton = (
  <span style={{
    display: 'inline-block',
    border: '1.2px solid #bbaee0',
    borderRadius: 8,
    padding: '1px 8px',
    color: '#492099',
    fontWeight: 500,
    fontSize: 14,
    background: '#f8f7fc',
    margin: '0 2px',
    verticalAlign: 'middle',
    lineHeight: 1.1,
    boxShadow: 'none',
  }}>Full Graph</span>
);

const ExploreGraphImg = (
  <AccountTreeIcon sx={{ color: '#5C3B8B', fontSize: 22, verticalAlign: 'middle', margin: '0 2px' }} />
);

const instructions = [
  {
    section: 'Write and refine notes',
    items: [
      <>Write your note in the editor. When finished, click <MagicIcon /> <b>Refine</b> to see the note subgraph and summary.</>,
    ]
  },
  {
    section: 'View graph',
    items: [
      <>Explore the visual graph in the small view on the left, or {ExploreGraphImg} to see the graph fully.</>,
      <>Only the entities for the current note are visible. Click {FullGraphButton} to see the full knowledge graph.</>
    ]
  },
  {
    section: 'Explore',
    items: [
      <>Select an {EntityDot}entity to see all notes related to it.</>,
      <>Double click an {EntityDot}entity to bring up all its neighbours.</>,
      <>Click {ExpandIcon} to see the full note. {CollapseIcon} to see the note summary.</>,
      "Select a note to view the subgraph.",
      "Double click a note to open in editor."
    ]
  }
];

const HelpButton = ({ headerMode }) => {
  const [toastOpen, setToastOpen] = useState(false);
  const [hasFlashed, setHasFlashed] = useState(false);

  // Check sessionStorage on mount
  useEffect(() => {
    if (sessionStorage.getItem('helpButtonPressed')) {
      setHasFlashed(true);
    }
  }, []);

  const handleClick = () => {
    setToastOpen(true);
    setHasFlashed(true);
    sessionStorage.setItem('helpButtonPressed', 'true');
  };

  // Flash animation: pulse until clicked
  const flashStyle = !hasFlashed ? {
    animation: 'help-flash 1.2s infinite',
    boxShadow: '0 0 0 0.2rem #e0d2ff',
  } : {};

  return (
    <>
      {/* Header Mode: Inline Icon Button */}
      {headerMode ? (
        <button
          aria-label="Show help"
          onClick={handleClick}
          style={{
            background: 'none',
            border: 'none',
            color: '#492099',
            fontSize: 26,
            cursor: 'pointer',
            marginRight: 2,
            padding: 4,
            borderRadius: 6,
            transition: 'background 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 40,
            width: 40,
            ...flashStyle,
          }}
          title="Show help"
        >
          <span style={{ fontWeight: 700, fontSize: 22, lineHeight: 1 }}>?</span>
        </button>
      ) : (
        // Floating FAB for non-header mode
        <button
          aria-label="Show help"
          onClick={handleClick}
          className="help-fab"
        >
          ?
        </button>
      )}
      {/* Toast Snackbar */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          top: 70,
          right: 24,
          left: 'auto',
          maxWidth: 400,
        }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="info"
          sx={{ width: '100%', background: '#fff', color: '#492099', border: '1px solid #492099', boxShadow: '0 2px 16px rgba(73,32,153,0.10)' }}
        >
          <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 22 }}>Welcome to NoteRefinery</div>
          {instructions.map((section, idx) => (
            <div key={section.section} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 600, color: '#492099', fontSize: 16, marginBottom: 2 }}>{section.section}</div>
              <div style={{ margin: 0, padding: 0 }}>
                {section.items.map((item, i) => (
                  <div key={i} style={{ marginBottom: 4, color: '#492099', fontSize: 15 }}>{item}</div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 18, marginBottom: 2 }}>
            <div style={{ fontWeight: 600, color: '#492099', fontSize: 16, marginBottom: 2 }}>Contact us</div>
            <div style={{ color: '#492099', fontSize: 14, fontWeight: 500 }}>
              For questions and feedback please email <a href="mailto:noterefinery@littleoxfordstreet.com" style={{ color: '#492099', textDecoration: 'underline' }}>noterefinery@littleoxfordstreet.com</a>
            </div>
          </div>
        </Alert>
      </Snackbar>
    </>
  );
};

// Add keyframes for the flash animation
if (typeof window !== 'undefined' && !document.getElementById('help-flash-keyframes')) {
  const style = document.createElement('style');
  style.id = 'help-flash-keyframes';
  style.innerHTML = `
    @keyframes help-flash {
      0% { box-shadow: 0 0 0 0.2rem #e0d2ff; }
      50% { box-shadow: 0 0 0 0.6rem #bbaee0; }
      100% { box-shadow: 0 0 0 0.2rem #e0d2ff; }
    }
  `;
  document.head.appendChild(style);
}

export default HelpButton;
